import { useState } from "react";
import {
  Card,
  Dropdown,
  Button,
  Badge,
  Popover,
  OverlayTrigger,
} from "react-bootstrap";
import RecipeForm from "./RecipeForm";
import ShareRecipes from "./ShareRecipes";
import ChemicalInfo from "./ChemicalInfo";
import ConfirmationPopup from "components/common/ConfirmationPopup";
import { useDispatch, useSelector } from "react-redux";
import { setEditedRecipe, setRecipes } from "reduxStore/slices/recipeSlice";
import RecipeService from "services/RecipeService";
import {
  RECIPE_DELETE_ERROR,
  ARE_YOU_SURE_ARCHIVE_RECIPE,
  RECIPE_HAS_BEEN_DELETED_SUCCESSFULLY,
  RECIPE_HAS_BEEN_ARCHIVED_SUCCESSFULLY,
  RECIPE_ARCHIVE_ERROR,
  ARE_YOU_SURE_UNARCHIVE_RECIPE,
  RECIPE_HAS_BEEN_UNARCHIVED_SUCCESSFULLY,
  ARE_YOU_SURE_DELETE_RECIPE,
} from "configurations/MessageConstants";
import Notes from "components/common/notes/Notes";
import {
  DATE_FORMAT,
  MODE_TYPES,
  NOTES_TYPES,
  SHARE_TYPES,
} from "configurations/Constants";
import { toast } from "react-toastify";
import { format } from "date-fns";

const ChemicalBadge = ({ chemical }) => {
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Popover id={`popover-${chemical.chemicalId}`}>
          <ChemicalInfo chemicalId={chemical.chemicalId} />
        </Popover>
      }
    >
      <Badge pill className="chemical-badge">
        <small>{`${chemical.name} ${chemical.quantity} ${chemical.unit}`}</small>
      </Badge>
    </OverlayTrigger>
  );
};

const RecipeCard = ({ recipe }) => {
  const dispatch = useDispatch();
  const recipes = useSelector((state) => state.recipe.recipes);
  const [showRecipeForm, setShowRecipeForm] = useState(false);
  const [showShareRecipe, setShowShareRecipe] = useState(false);
  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showAllChemicals, setShowAllChemicals] = useState(false);

  const handleEditClick = () => {
    dispatch(setEditedRecipe(recipe));
    setShowRecipeForm(true);
  };

  const handleShareClick = () => {
    setShowShareRecipe(true);
  };

  const handleArchiveClick = () => {
    setShowArchiveConfirmation(true);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await RecipeService.deleteRecipe(recipe.id);
      setShowDeleteConfirmation(false);
      dispatch(
        setRecipes(
          recipes.filter((deletedRecipe) => deletedRecipe.id !== recipe.id)
        )
      );
      toast.success(RECIPE_HAS_BEEN_DELETED_SUCCESSFULLY);
    } catch (error) {
      console.error(RECIPE_DELETE_ERROR, error);
      toast.error(error.response.data.detail);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmArchiveToggle = async () => {
    setIsArchiving(true);
    try {
      const response = await RecipeService.updateRecipe(
        { archived: !recipe.archived },
        recipe.id
      );
      setShowArchiveConfirmation(false);
      dispatch(
        setRecipes(
          recipes.map((recipe) =>
            recipe.id === response.oldRecipe.id ? response.oldRecipe : recipe
          )
        )
      );
      toast.success(
        recipe.archived
          ? RECIPE_HAS_BEEN_UNARCHIVED_SUCCESSFULLY
          : RECIPE_HAS_BEEN_ARCHIVED_SUCCESSFULLY
      );
    } catch (error) {
      console.error(RECIPE_ARCHIVE_ERROR, error);
      toast.error(error.response.data.detail);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleShowNotes = () => {
    setShowNotes(true);
  };

  return (
    <>
      <Card
        className={`
      ${!recipe.isShared ? "recipe-card" : "shared-recipe-card"}
      `}
      >
        <Card.Body className="p-2">
          <div className="d-flex justify-content-between">
            <div className="text-truncate">
              <div className="text-truncate">{recipe.name}</div>
              <small className="text-muted">{recipe.type}</small>
            </div>
            <div className="ms-2 d-flex flex-column align-items-end">
              <small className="text-muted text-nowrap">
                {recipe.water} G/A
              </small>
              <div className="d-flex flex-row gap-2 my-1 justify-content-end">
                {!recipe.archived && (
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={handleShareClick}
                    aria-label="Share Recipe"
                  >
                    <i className="fa fa-share-alt" aria-hidden="true"></i>
                  </Button>
                )}
                <Dropdown align="end">
                  <Dropdown.Toggle
                    variant="outline-dark"
                    className="no-caret"
                    size="sm"
                    id={`dropdown-${recipe.id}`}
                    aria-label="Recipe actions"
                  >
                    <i className="fa fa-ellipsis-h" aria-hidden="true"></i>
                  </Dropdown.Toggle>
                  <Dropdown.Menu style={{ minWidth: "fit-content" }}>
                    <Dropdown.Item onClick={handleEditClick}>
                      {recipe.archived ? (
                        <>
                          <i className="fa fa-eye me-2" aria-hidden="true"></i>
                          View
                        </>
                      ) : (
                        <>
                          <i className="fa fa-edit me-2" aria-hidden="true"></i>
                          Edit
                        </>
                      )}
                    </Dropdown.Item>
                    {!recipe.archived && (
                      <Dropdown.Item onClick={handleShowNotes}>
                        <i
                          className="fa fa-sticky-note me-2"
                          aria-hidden="true"
                        ></i>
                        Notes
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item onClick={handleArchiveClick}>
                      {recipe.archived ? (
                        <>
                          <i
                            className="fa fa-archive me-2"
                            aria-hidden="true"
                          ></i>
                          Unarchive
                        </>
                      ) : (
                        <>
                          <i
                            className="fa fa-archive me-2"
                            aria-hidden="true"
                          ></i>
                          Archive
                        </>
                      )}
                    </Dropdown.Item>
                    {recipe.archived && (
                      <Dropdown.Item onClick={handleDeleteClick}>
                        <i className="fa fa-trash me-2"></i> Delete
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </div>
          <div>
            <div>
              <div
                className="d-flex justify-content-between align-items-center mt-1 cursor-pointer"
                onClick={() => setShowAllChemicals(!showAllChemicals)}
              >
                <small className="text-muted chemicals-added-label">
                  {recipe.chemicals.length}{" "}
                  {recipe.chemicals.length === 1
                    ? "chemical added"
                    : "chemicals added"}
                </small>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-decoration-none chemicals-toggle-btn"
                >
                  <small className="text-uppercase fw-semibold text-muted">
                    {showAllChemicals ? "Hide" : "View"} Chemicals
                  </small>
                  <i
                    className={`fa fa-chevron-${
                      showAllChemicals ? "up" : "down"
                    } ms-2`}
                  ></i>
                </Button>
              </div>
              {showAllChemicals && (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {recipe.chemicals.map((chemical) => (
                    <ChemicalBadge
                      key={chemical.chemicalId}
                      chemical={chemical}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card.Body>

        <div className="border-top py-1">
          <div className="d-flex justify-content-between align-items-center mx-2">
            <div className="d-flex gap-2">
              {recipe.version > 1 && (
                <small className="recipe-version">
                  Version {recipe.version}
                </small>
              )}
              <small className="recipe-created-date">
                {format(new Date(recipe.created), `${DATE_FORMAT} HH:mm`)}
              </small>
            </div>
            {recipe.sharedBy && (
              <small className="me-2 shared-by-recipe-label">
                Shared by <strong>{recipe.sharedByName}</strong>
              </small>
            )}
          </div>
        </div>
      </Card>
      {showRecipeForm && (
        <RecipeForm
          mode={MODE_TYPES.EDIT}
          show={showRecipeForm}
          onClose={() => setShowRecipeForm(false)}
        />
      )}
      <ShareRecipes
        type={SHARE_TYPES.SINGLE}
        recipes={[recipe]}
        showShareRecipe={showShareRecipe}
        onClose={() => setShowShareRecipe(false)}
      />
      <ConfirmationPopup
        show={showArchiveConfirmation}
        title={recipe.archived ? "Unarchive Recipe" : "Archive Recipe"}
        message={
          <span className="text-muted">
            {recipe.archived
              ? ARE_YOU_SURE_UNARCHIVE_RECIPE
              : ARE_YOU_SURE_ARCHIVE_RECIPE}
            <strong> {recipe.name}</strong>
          </span>
        }
        onConfirm={handleConfirmArchiveToggle}
        onCancel={() => setShowArchiveConfirmation(false)}
        confirmText={recipe.archived ? "Unarchive" : "Archive"}
        cancelText="Cancel"
        isDeleting={isArchiving}
      />
      <ConfirmationPopup
        show={showDeleteConfirmation}
        title="Delete Recipe"
        message={
          <span className="text-muted">
            {ARE_YOU_SURE_DELETE_RECIPE} <strong>{recipe.name}</strong>?
          </span>
        }
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirmation(false)}
        confirmText="Delete"
        cancelText="Cancel"
        isDeleting={isDeleting}
      />
      {showNotes && (
        <Notes
          id={recipe.id}
          type={NOTES_TYPES.RECIPE}
          name={recipe.name}
          show={showNotes}
          handleClose={() => setShowNotes(false)}
        />
      )}
    </>
  );
};

export default RecipeCard;
